import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { zonedTimeToUtc } from 'date-fns-tz';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	getRoundingDuration,
	type PercentPosition,
	roundToMultiple,
} from '@atlassian/jira-software-deployments-common-table/src/components/table/common/utils/timeline';
import type { TimelineMode } from '@atlassian/jira-software-deployments-common-table/src/model/timeline';
import {
	type Deployment,
	type DeploymentState,
	type DeploymentEnvironment,
	DeploymentEnvironmentTypeOptions,
	DeploymentStateOptions,
} from '../../types';

const priorityList = [
	{ state: DeploymentStateOptions.FAILED },
	{ state: DeploymentStateOptions.IN_PROGRESS },
	{ state: DeploymentStateOptions.PENDING },
	{ state: DeploymentStateOptions.ROLLED_BACK },
	{ state: DeploymentStateOptions.SUCCESSFUL, prodOnly: true },
	{ state: DeploymentStateOptions.SUCCESSFUL, nonProdOnly: true },
	{ state: DeploymentStateOptions.UNKNOWN },
	{ state: DeploymentStateOptions.CANCELLED },
];

// @ts-expect-error - TS7006 - Parameter 'deployment' implicitly has an 'any' type.
const findPriorityNumber = (deployment) =>
	priorityList.findIndex((priority) => {
		if (
			priority.prodOnly &&
			deployment.environment.type !== DeploymentEnvironmentTypeOptions.PRODUCTION
		) {
			return false;
		}
		if (
			priority.nonProdOnly &&
			deployment.environment.type === DeploymentEnvironmentTypeOptions.PRODUCTION
		) {
			return false;
		}
		return deployment.state === priority.state;
	});

export const getEnvironmentId = (environment: DeploymentEnvironment): string =>
	`${environment.type}:${environment.displayName}`;

// get the most meaningful deployment from a list of deployments on the same day
// i.e. the deployment to show to then user. e.g. if there's a FAILED deployment
// and a SUCCESSFUL deployment from different environments, show the red FAILED icon.
// NOTE: deployments are sorted most recent to oldest
export const getMostMeaningfulDeployment = (timeSortedDeployments: Deployment[]) => {
	if (timeSortedDeployments.length === 1) return timeSortedDeployments[0];

	const deploysByEnvironment: Deployment[][] = Object.values(
		groupBy(timeSortedDeployments, (deployment) => getEnvironmentId(deployment.environment)),
	);

	const mostRecentDeploymentsPerEnvironment = deploysByEnvironment.map((deploys) => deploys[0]);

	const prioritySortedDeployments = sortBy(mostRecentDeploymentsPerEnvironment, (deployment) =>
		findPriorityNumber(deployment),
	);

	return prioritySortedDeployments[0];
};

export const getColorForDeploymentState = (deploymentState: DeploymentState): string => {
	const state = { type: deploymentState };
	switch (state.type) {
		case DeploymentStateOptions.PENDING:
			return token('color.icon.information', colors.B300);
		case DeploymentStateOptions.IN_PROGRESS:
			return token('color.icon.information', colors.B300);
		case DeploymentStateOptions.SUCCESSFUL:
			return token('color.icon.success', colors.G300);
		case DeploymentStateOptions.CANCELLED:
			return token('color.icon.warning', colors.Y300);
		case DeploymentStateOptions.FAILED:
			return token('color.icon.danger', colors.R400);
		case DeploymentStateOptions.ROLLED_BACK:
			return token('color.icon.warning', colors.Y400);
		case DeploymentStateOptions.UNKNOWN:
			return token('color.icon.success', colors.G300);
		default:
			throw Error('Unhandled deployment state');
	}
};

export type GroupedDeployments = {
	deployments: Deployment[];
} & PercentPosition;

export const getGroupedDeployments = (
	deployments: Deployment[],
	getPositionFromDate: (date: Date) => PercentPosition,
	mode: TimelineMode,
): GroupedDeployments[] => {
	const daysToDeployments = groupBy(deployments, (deployment) => {
		const roundingDuration = getRoundingDuration(mode);
		return roundToMultiple(
			zonedTimeToUtc(deployment.lastUpdated, 'utc').valueOf(),
			roundingDuration,
		);
	});

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const grouped: Deployment[][] = Object.values(daysToDeployments) as any;
	return grouped.map((deploymentsOnDay: Deployment[]) => ({
		deployments: deploymentsOnDay,
		...getPositionFromDate(deploymentsOnDay[0].lastUpdated),
	}));
};

/**
 * This comparision is under assumption; deployment array reference could be changed but the order of the deployment is not changed, which is true.
 */
export const areDeploymentsEqual = (
	sortedDeps1: Deployment[],
	sortedDeps2: Deployment[],
): boolean => {
	if (sortedDeps1.length !== sortedDeps2.length) {
		return false;
	}

	return sortedDeps1.every((deployment, index) => deployment.id === sortedDeps2[index].id);
};

// once we go 100% AGG we should be able to stop sorting on the frontend.
// suggest we put frontend sorting behind it's own FF just incase.
export const getOrderedDeploymentsByTime = (deployments: Deployment[]): Deployment[] =>
	[...deployments].sort(
		(d1: Deployment, d2: Deployment) => d2.lastUpdated.getTime() - d1.lastUpdated.getTime(),
	);
